import * as React from 'react';
import {
  NotificationBellIcon,
  ChevronDownIcon,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  IconButton,
  ThemeSunIcon,
  ThemeMoonIcon,
  FlagRussiaIcon,
  FlagUzbekistanIcon,
  FlagUkIcon,
} from '@noor/ui';
import { useTranslation } from 'react-i18next';
import { CheckIcon, KeyRoundIcon, LogOutIcon, MenuIcon } from 'lucide-react';
import { cn, useUiStore, LANGUAGES, type Language } from '@noor/lib';
import { useSession } from '@/entities/session';
import { useSignOut } from '@/features/sign-out';
import { ChangePasswordDialog } from '@/features/change-password';
import { HeaderSearch } from '@/features/header-search';
import { AppVersionBadge } from '@/shared/ui/app-version-badge';

export interface DashboardHeaderProps {
  className?: string;
  onOpenSidebar?: () => void;
}

function getInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'A'
  );
}

/**
 * Figma 732:5020 — 68×32 theme switch. Sun left / moon right; the active side
 * gets the brand-orange disc, the idle side stays dimmed.
 */
function ThemeSwitch() {
  const { t } = useTranslation('common');
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const dark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-pressed={dark}
      aria-label={dark ? t('theme.toLight') : t('theme.toDark')}
      className="flex h-8 w-[68px] shrink-0 cursor-pointer items-center justify-between rounded-full bg-[#f3f4f6] px-1 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40 dark:bg-white/10"
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-6 items-center justify-center rounded-full transition-colors',
          dark ? 'text-[#94a3b8]' : 'bg-primary text-primary-foreground',
        )}
      >
        <ThemeSunIcon className="size-4" />
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'flex size-6 items-center justify-center rounded-full transition-colors',
          dark ? 'bg-primary text-primary-foreground' : 'text-[#94a3b8]',
        )}
      >
        <ThemeMoonIcon className="size-4" />
      </span>
    </button>
  );
}

const FLAG_BY_LANGUAGE: Record<Language, typeof FlagRussiaIcon> = {
  ru: FlagRussiaIcon,
  uz: FlagUzbekistanIcon,
  en: FlagUkIcon,
};

/** Figma 732:5020 — current-language flag + chevron trigger. */
function LanguageTrigger() {
  const { t, i18n } = useTranslation('common');
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const Flag = FLAG_BY_LANGUAGE[language];

  const change = (lng: Language) => {
    setLanguage(lng);
    void i18n.changeLanguage(lng);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('language.label')}
          className="flex shrink-0 cursor-pointer items-center gap-0.5 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <Flag className="size-5" />
          <ChevronDownIcon className="size-5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((lng) => (
          <DropdownMenuItem key={lng} onClick={() => change(lng)}>
            <CheckIcon
              className={cn('size-4', lng === language ? 'opacity-100' : 'opacity-0')}
            />
            {t(`language.${lng}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DashboardHeader({ className, onOpenSidebar }: DashboardHeaderProps) {
  const { t } = useTranslation('common');
  const { data: session } = useSession();
  const signOut = useSignOut();
  const [hasNotification, setHasNotification] = React.useState(true);
  const [passwordOpen, setPasswordOpen] = React.useState(false);

  const fullName =
    [session?.first_name, session?.last_name].filter(Boolean).join(' ') || 'Admin User';
  const role = session?.role ?? 'ADMIN';

  return (
    <header
      className={cn(
        'flex h-16 w-full shrink-0 items-center justify-between gap-3 bg-white px-4 shadow-xs sm:px-6 dark:bg-card',
        className,
      )}
    >
      {/* Global search (pages bind to it via `useHeaderSearch`). */}
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          aria-label="Открыть меню"
          onClick={onOpenSidebar}
          className="flex size-9 shrink-0 items-center justify-center rounded-md md:hidden"
        >
          <MenuIcon className="size-5" />
        </button>
        <HeaderSearch className="max-w-full min-w-0" />
      </div>

      {/* Version, theme switch, language, notifications, user — 24px gaps. */}
      <div className="flex items-center gap-2 sm:gap-6">
        <span className="hidden sm:inline">
          <AppVersionBadge />
        </span>

        <span className="hidden sm:inline-flex">
          <ThemeSwitch />
        </span>

        <span className="hidden sm:inline-flex">
          <LanguageTrigger />
        </span>

        {/* Notification Bell */}
        <div className="relative hidden shrink-0 sm:block">
          <IconButton
            variant="ghost"
            size="sm"
            aria-label="Уведомления"
            className="size-10 rounded-full text-muted-foreground hover:text-foreground"
            onClick={() => setHasNotification(false)}
          >
            <NotificationBellIcon className="size-6" />
          </IconButton>
          {hasNotification && (
            <span
              className="absolute top-[10px] right-[10px] size-2 rounded-full bg-[#ef4444]"
              aria-hidden="true"
            />
          )}
        </div>

        {/* User Avatar + Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex shrink-0 cursor-pointer items-center gap-2.5 rounded-full p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label={fullName}
            >
              <Avatar className="size-10 border border-border/60">
                <AvatarFallback className="bg-primary/10 text-sm font-medium text-primary">
                  {getInitials(fullName)}
                </AvatarFallback>
              </Avatar>
              <ChevronDownIcon className="size-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <span className="block text-sm font-medium text-foreground">
                {fullName}
              </span>
              <span className="block text-xs text-muted-foreground capitalize">
                {role}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setPasswordOpen(true)}>
              <KeyRoundIcon className="mr-2 size-4" />
              <span>{t('auth.changePassword')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void signOut()}
              className="text-destructive focus:text-destructive"
            >
              <LogOutIcon className="mr-2 size-4" />
              <span>{t('auth.signOut', 'Выйти')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {passwordOpen && (
        <ChangePasswordDialog
          open={passwordOpen}
          onClose={() => setPasswordOpen(false)}
        />
      )}
    </header>
  );
}
